"use client";

import React, { useEffect, useState } from "react";
import { useBreakpointV3 } from "~/hooks/useIsMobile";
import { useLeftSideBarStore } from "~/state/store";
import { cn } from "~/utils/fontsConfig";
import { If } from "../Conditional";
import EventViewSidebar from "../events-components/EventViewSidebar";
import { api } from "~/utils/api";
import ShareEventModal from "~/app/events/[slug]/share/_components/ShareEventModal";
import { usePermissionsContext } from "~/app/providers";
import { EventBySlugOutput } from "~/types/EventTypesInferred";

type Props = {
    children: React.ReactNode;
    event: EventBySlugOutput;
};

const EventViewLayout = ({ children, event }: Props) => {
    const isDesktop = useBreakpointV3("lg");
    const { sideBarState, shareModalOpen, setShareModalOpen } =
        useLeftSideBarStore();
    const isMobileSidebarVisible = sideBarState === "event-management";
    const permissions = usePermissionsContext();

    // put permission data into client cache since it's used heavily
    const utils = api.useUtils();
    utils.permissions.getUserPermissions.setData(
        {
            adminCheck: true,
            community: { fromEventSlug: event.slug },
            event: { slug: event.slug },
        },
        permissions,
    );
    utils.permissions.getUserPermissions.setData(
        {
            adminCheck: true,
            event: { slug: event.slug },
        },
        permissions,
    );

    //*: we should be managing permissions at parent (here) and not showing children entirely to avoid unnecessary computation.
    //*: left sidebar permission managment state logic in child -> EventViewSidebar to avoid breaking anything.
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);

    useEffect(() => {
        if (
            permissions?.isManager ||
            permissions?.isAdmin ||
            permissions?.isHost
        ) {
            setIsSidebarVisible(true);
        } else setIsSidebarVisible(false);
    }, [permissions]);

    return (
        <div className="z-50 flex h-full w-full">
            {/* Mobile Sidebar */}
            <If condition={!isDesktop && isMobileSidebarVisible}>
                <div className="fixed inset-0 mt-16">
                    <EventViewSidebar />
                </div>
            </If>

            <div
                className={cn(
                    "grid grid-cols-1 gap-2 lg:grid-cols-5",
                    "mx-auto w-full max-w-[90rem] md:px-4 md:py-8",
                    isMobileSidebarVisible && !isDesktop && "-z-10 filter",
                    "self-start",
                )}
            >
                <div className="hidden w-full lg:block">
                    <EventViewSidebar />
                </div>

                <div
                    className={cn(
                        "max-w-full",
                        isSidebarVisible ? "lg:col-span-4" : "lg:col-span-5",
                    )}
                >
                    {children}
                </div>
            </div>

            <ShareEventModal
                isVisible={shareModalOpen}
                setIsVisible={setShareModalOpen}
                event={{ ...event, walletName: null }}
            />
        </div>
    );
};

export default EventViewLayout;
